import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { RiLockPasswordLine } from '@remixicon/react';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@marine/ui/card';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { api } from '@marine/api-client/api';
import { toast } from 'sonner';

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  password: z.string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[a-z]/, 'Must include lowercase letter')
    .regex(/[A-Z]/, 'Must include uppercase letter')
    .regex(/\d/, 'Must include number')
    .regex(/[^a-zA-Z\d]/, 'Must include special character'),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ['password_confirmation'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export function SecurityCard() {

  
  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      password: '',
      password_confirmation: '',
    },
  });

  const updatePassword = useMutation({
    mutationFn: (data: PasswordFormData) => 
      api.put('/api/user/password', data),
    onSuccess: () => {
      toast.success('Password updated successfully');
      form.reset();
    },
    onError: (error: any) => {
      const message = error.response?.data?.errors?.password?.[0] || 
                     error.response?.data?.errors?.current_password?.[0] ||
                     'Failed to update password';
      toast.error(message);
    },
  });

  const onSubmit = (data: PasswordFormData) => {
    updatePassword.mutate(data);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <RiLockPasswordLine className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>
              Passwords must be at least 12 characters and cannot match your last 5 passwords
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              {...form.register('current_password')}
              disabled={updatePassword.isPending}
            />
            {form.formState.errors.current_password && (
              <p className="text-sm text-red-500">{form.formState.errors.current_password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              {...form.register('password')}
              disabled={updatePassword.isPending}
            />
            <PasswordStrengthMeter 
              password={form.watch('password')} 
              className="mt-2"
            />
            {form.formState.errors.password && (
              <p className="text-sm text-red-500">{form.formState.errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmation">Confirm New Password</Label>
            <Input
              id="password_confirmation"
              type="password"
              {...form.register('password_confirmation')}
              disabled={updatePassword.isPending}
            />
            {form.formState.errors.password_confirmation && (
              <p className="text-sm text-red-500">{form.formState.errors.password_confirmation.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={updatePassword.isPending}
            className="w-full sm:w-auto"
          >
            {updatePassword.isPending ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
